'use client';

/**
 * API namespace `admin.market.giftCards` (sub-namespace extracted).
 * Verbatim extraction from frontend/src/lib/api-client.ts (originally within admin.market).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2 (relative to admin.market), T-EXPORT, T-IMPORTS.
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';
import type {
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
} from '../core/types';
import type {
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  Paginated,
} from '../core/shared-types';

export const giftCards = {
  /**
   * GET /api/admin/market/gift-cards — admin list of ALL gift cards
   * across ALL sellers (paginated, 1-indexed). Returns
   * `GiftCardListResponse` (`giftCards: GiftCardResponse[]` + `total`
   * + `page` + `limit`). Each gift card in the list is the EXPANDED
   * snapshot (includes the seller's storeName + storeSlug snapshot —
   * same wire shape as the public catalog response per SPEC-242 §3.5).
   *
   * Query params (all optional): same shape as `coupons.list` BUT
   * the `status` filter accepts `GiftCardStatus` (4 values:
   * pending_approval / approved / rejected / paused — NO 'expired'
   * value because gift cards have no countdown timer on the
   * GiftCard entity itself per SPEC-242 §3.4.2 — the per-purchase
   * `GiftCardPurchase.expiresAt` is the canonical expiration
   * marker, NOT the GiftCard).
   *
   * Authz: `requireAuth` + `requireAdminRead` (router-level). NO
   * 2FA required for the GET (read-only).
   *
   * Spec ref: SPEC-242 §3.5 (GET /admin/market/gift-cards), §3.6.5
   *           (admin UI — table of gift cards awaiting moderation).
   */
  async list(
    filters?: {
      page?: number;
      limit?: number;
      status?: GiftCardStatus;
      sellerId?: string;
      includeInactive?: boolean;
    },
    signal?: AbortSignal,
  ): Promise<GiftCardListResponse> {
    const qs = new URLSearchParams();
    if (filters?.page !== undefined) qs.set('page', String(filters.page));
    if (filters?.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.sellerId) qs.set('sellerId', filters.sellerId);
    if (filters?.includeInactive !== undefined) {
      qs.set('includeInactive', String(filters.includeInactive));
    }
    const query = qs.toString();
    return apiFetch<GiftCardListResponse>(
      query ? `/admin/market/gift-cards?${query}` : '/admin/market/gift-cards',
      { signal },
    );
  },
  /**
   * PUT /api/admin/market/gift-cards/:giftCardId/approve — approve a
   * pending gift card (INV-MKT-20). The backend atomically
   * transitions `status: 'pending_approval' → 'approved'` via CAS.
   *
   * AuditLog 'GIFT_CARD_APPROVED' is written (INV-SEC-08 hash chain).
   * NO UnifiedTransaction — gift cards do NOT create financial
   * transactions on approve/reject (per task constraint — the
   * financial flow is buyer-side via `purchaseGiftCard`).
   *
   * 2FA required (INV-SEC-06 §9.6 — per-route `requireRecent2FA` in
   * `routes/admin.route.ts`). On 403 `2FA_REQUIRED`, the caller's
   * catch block re-opens the `TwoFactorFinancialDialog` (rendered
   * by `/admin/market/gift-cards/page.tsx`).
   *
   * Path param: `giftCardId` is the `GiftCard.giftCardId` (UUID).
   * The backend accepts an EMPTY body (no notes field).
   *
   * Spec ref: SPEC-242 §3.5 (PUT /admin/market/gift-cards/:giftCardId
   *           /approve), §3.4.2 (INV-MKT-20 admin approval gate);
   *           SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06).
   */
  async approve(
    giftCardId: string,
    signal?: AbortSignal,
  ): Promise<GiftCard> {
    return apiFetch<GiftCard>(
      `/admin/market/gift-cards/${encodeURIComponent(giftCardId)}/approve`,
      {
        method: 'PUT',
        body: JSON.stringify({}),
        signal,
      },
    );
  },
  /**
   * PUT /api/admin/market/gift-cards/:giftCardId/reject — reject a
   * pending gift card (INV-MKT-20). The backend atomically
   * transitions `status: 'pending_approval' → 'rejected'` via CAS.
   * The `reason` is MANDATORY (Zod `rejectReasonSchema` — 10-500
   * chars). The reason is recorded in the AuditLog metadata (the
   * `GiftCard` model has no `adminNotes` field per SPEC-242 §3.3.3).
   *
   * 2FA required (INV-SEC-06 §9.6 — same rationale as `approve`).
   *
   * Path param: `giftCardId` is the `GiftCard.giftCardId` (UUID).
   *
   * Spec ref: SPEC-242 §3.5 (PUT /admin/market/gift-cards/:giftCardId
   *           /reject), §3.4.2 (INV-MKT-20 admin approval gate);
   *           SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06).
   */
  async reject(
    giftCardId: string,
    reason: string,
    signal?: AbortSignal,
  ): Promise<GiftCard> {
    return apiFetch<GiftCard>(
      `/admin/market/gift-cards/${encodeURIComponent(giftCardId)}/reject`,
      {
        method: 'PUT',
        body: JSON.stringify({ reason }),
        signal,
      },
    );
  },
};
